import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listAiProviders, updateAiProvider, testAiProvider, type AiProviderConfig } from "@/api/ai-providers";
import { cn } from "@/cn";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";

export function SettingsAiPage() {
  const qc = useQueryClient();

  const { data: providers = [], isLoading } = useQuery<AiProviderConfig[]>({
    queryKey: ["ai-providers"],
    queryFn: listAiProviders,
    staleTime: 30_000,
  });

  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(providers);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { status: "success" | "error" | "testing"; message: string }>>({});

  const saveMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AiProviderConfig> }) => updateAiProvider(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-providers"] }),
  });

  const testMutation = useMutation({
    mutationFn: async (provider: AiProviderConfig) => {
      setTestResults((prev) => ({ ...prev, [provider.providerId]: { status: "testing", message: "Testing..." } }));
      return testAiProvider(provider.providerId, {
        apiKey: provider.apiKey,
        baseUrl: provider.baseUrl,
        model: provider.defaultModel,
      });
    },
    onSuccess: (data, provider) => {
      setTestResults((prev) => ({ ...prev, [provider.providerId]: { status: data.success ? "success" : "error", message: data.response } }));
    },
    onError: (err, provider) => {
      setTestResults((prev) => ({ ...prev, [provider.providerId]: { status: "error", message: err instanceof Error ? err.message : "Test failed" } }));
    },
  });

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">AI Providers</h1>
        <p className="text-muted-foreground">
          Configure the AI provider used by CEO Command and the AI Assistant.
          Enable one provider and set your API key.
        </p>
      </header>

      {isLoading && <div className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" /></div>}

      {!isLoading && (
        <>
          <div className="grid gap-4">
            {pageItems.map((provider) => {
            const isEditing = editingId === provider.providerId;
            const result = testResults[provider.providerId];
            return (
              <div key={provider.providerId} className={cn("rounded-lg border bg-card transition", provider.enabled ? "border-border" : "border-border/50 opacity-60")}>
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className={cn("flex h-8 w-8 items-center justify-center rounded-md text-xs font-bold", provider.enabled ? "bg-brand text-brand-foreground" : "bg-muted text-muted-foreground")}>
                      {provider.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{provider.name}</p>
                      <p className="text-xs text-muted-foreground">{provider.providerId}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => testMutation.mutate(provider)}
                      disabled={testMutation.isPending}
                      className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-50"
                    >
                      {testResults[provider.providerId]?.status === "testing" ? "Testing..." : "Test"}
                    </button>
                    <button
                      onClick={() => setEditingId(isEditing ? null : provider.providerId)}
                      className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
                    >
                      {isEditing ? "Done" : "Configure"}
                    </button>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={provider.enabled}
                        onChange={() => saveMutation.mutate({ id: provider.providerId, data: { enabled: !provider.enabled } })}
                        className="peer sr-only"
                      />
                      <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
                    </label>
                  </div>
                </div>

                {result && result.status !== "testing" && (
                  <div className={cn("border-t border-border px-4 py-2", result.status === "success" ? "text-success" : "text-destructive")}>
                    <p className="text-xs">{result.message}</p>
                  </div>
                )}

                {isEditing && (
                  <div className="border-t border-border p-4 space-y-3">
                    <TextField provider={provider} field="apiKey" label="API Key" placeholder="sk-..." secret onSave={(id, data) => saveMutation.mutate({ id, data })} />
                    <TextField provider={provider} field="baseUrl" label="Base URL" placeholder="https://..." onSave={(id, data) => saveMutation.mutate({ id, data })} />
                    <TextField provider={provider} field="defaultModel" label="Default Model" placeholder="gpt-4o-mini" onSave={(id, data) => saveMutation.mutate({ id, data })} />
                  </div>
                )}
              </div>
            );
            })}
          </div>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            totalItems={providers.length}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
}

function TextField({ provider, field, label, placeholder, secret, onSave }: {
  provider: AiProviderConfig;
  field: "apiKey" | "baseUrl" | "defaultModel";
  label: string;
  placeholder: string;
  secret?: boolean;
  onSave: (id: string, data: Partial<AiProviderConfig>) => void;
}) {
  const [value, setValue] = useState((provider as any)[field] ?? "");
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onSave(provider.providerId, { [field]: value });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="flex items-center gap-2">
      <label className="w-24 text-xs font-medium text-foreground shrink-0">{label}</label>
      <input
        type={secret ? "password" : "text"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="flex-1 rounded-md border border-border bg-bg px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
      />
      <button
        onClick={handleSave}
        className="rounded-md bg-brand px-2 py-1.5 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
      >
        {saved ? "Saved" : "Save"}
      </button>
    </div>
  );
}